import { useState, useEffect } from 'react';
import axios from 'axios';
import { theme, spacing, typography, mobileSpacing } from '../styles/theme';
import { Sentry } from '../sentry';

export default function CardAutocomplete({ inputValue, cursorPosition, onSelectCard }) {
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Extract the current card name being typed
  const getCurrentCardInput = () => {
    const textBeforeCursor = inputValue.substring(0, cursorPosition);
    const lastOpenBracket = textBeforeCursor.lastIndexOf('[[');
    
    if (lastOpenBracket === -1) return null;
    
    const textAfterBracket = textBeforeCursor.substring(lastOpenBracket + 2);
    const hasCloseBracket = textAfterBracket.includes(']]');
    
    if (hasCloseBracket) return null;
    
    return {
      query: textAfterBracket,
      startPos: lastOpenBracket
    };
  };

  // Fetch card suggestions from Scryfall
  useEffect(() => {
    const cardInput = getCurrentCardInput();
    
    if (!cardInput || cardInput.query.length < 3) {
      setSuggestions([]);
      return;
    }

    const fetchSuggestions = async () => {
      setIsLoading(true);
      try {
        // Scryfall autocomplete endpoint
        const response = await axios.get(
          `https://api.scryfall.com/cards/autocomplete?q=${encodeURIComponent(cardInput.query)}`
        );
        setSuggestions(response.data.data.slice(0, 5)); // Top 5 suggestions
        setSelectedIndex(0);
      } catch (error) {
        console.error('Error fetching card suggestions:', error);
        Sentry.captureException(error, {
            tags: {
                component: 'CardAutocomplete',
                query: cardInput.query,
            },
            level: 'warning',
        });
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    // Debounce to avoid too many API calls
    const timer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(timer);
  }, [inputValue, cursorPosition]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (suggestions.length === 0) return false;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % suggestions.length);
        return true;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
        return true;
      case 'Enter':
        if (suggestions[selectedIndex]) {
          e.preventDefault();
          onSelectCard(suggestions[selectedIndex]);
          setSuggestions([]);
          return true;
        }
        return false;
      case 'Escape':
        setSuggestions([]);
        return true;
      default:
        return false;
    }
  };

  // Expose handleKeyDown to parent
  useEffect(() => {
    window.cardAutocompleteKeyHandler = handleKeyDown;
    return () => {
      window.cardAutocompleteKeyHandler = null;
    }
  }, [suggestions, selectedIndex]); // Re-create handler when these change

  if (suggestions.length === 0) return null;

  return (
    <div style={{
      position: 'absolute',
      bottom: '100%',
      left: 0,
      right: 0,
      backgroundColor: 'white',
      border: `1px solid ${theme.border.light}`,
      borderRadius: '6px',
      boxShadow: theme.shadow.medium,
      marginBottom: spacing.sm,
      maxHeight: '200px',
      overflowY: 'auto',
      zIndex: 100,
      WebkitOverflowScrolling: 'touch',
      ...(window.innerWidth < 768 && {
        maxHeight: '150px',
      })
    }}>
      {isLoading ? (
        <div style={{ 
          padding: spacing.md, 
          color: theme.text.secondary,
          fontSize: typography.fontSize.sm 
        }}>
          Loading...
        </div>
      ) : (
        suggestions.map((cardName, index) => (
          <div
            key={index}
            onClick={() => {
              onSelectCard(cardName);
              setSuggestions([]);
            }}
            style={{
              padding: spacing.md,
              cursor: 'pointer',
              backgroundColor: index === selectedIndex ? theme.bg.secondary : 'white',
              fontSize: typography.fontSize.sm,
              color: theme.text.primary,
              borderBottom: index < suggestions.length - 1 ? `1px solid ${theme.border.light}` : 'none',
              minHeight: '44px',
              display: 'flex',
              alignItems: 'center',
              ...(window.innerWidth <= 768 && {
                padding: `${mobileSpacing.md} ${mobileSpacing.lg}`,
                fontSize: typography.mobileFontSize.sm,
                minHeight: '48px',
              })
            }}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            {cardName}
          </div>
        ))
      )}
    </div>
  );
}